import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Button, Table, Space, Form, Input, Select, Popconfirm, message } from 'antd';
import {
  FolderOutlined,
  FolderOpenOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { clientCategoriesApi, clientSubcategoriesApi, show422Error, ValidationError } from '../../utils/api';
import PermissionGate from '../PermissionGate';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';

const { Option } = Select;

/**
 * مدیریت دسته و زیردستهٔ مشتری داخل فرم افزودن/ویرایش مشتری (بدون تب سایدبار)
 */
const ClientTaxonomyManager = ({ onRefresh }) => {
  const [catPanelOpen, setCatPanelOpen] = useState(false);
  const [subPanelOpen, setSubPanelOpen] = useState(false);

  const [catLoading, setCatLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [catDeletePermission, setCatDeletePermission] = useState(null);
  const [catFormOpen, setCatFormOpen] = useState(false);
  const [catEditing, setCatEditing] = useState(null);
  const [catSubmitting, setCatSubmitting] = useState(false);
  const [catForm] = Form.useForm();

  const [subLoading, setSubLoading] = useState(false);
  const [subCategoriesList, setSubCategoriesList] = useState([]);
  const [subCategoriesFull, setSubCategoriesFull] = useState([]);
  const [subDeletePermission, setSubDeletePermission] = useState(null);
  const [filterCategoryId, setFilterCategoryId] = useState('all');
  const [subFormOpen, setSubFormOpen] = useState(false);
  const [subEditing, setSubEditing] = useState(null);
  const [subSubmitting, setSubSubmitting] = useState(false);
  const [subForm] = Form.useForm();

  const loadCategoriesPanel = useCallback(async () => {
    setCatLoading(true);
    try {
      const res = await clientCategoriesApi.getCreateData();
      const data = res?.data ?? res;
      setCategories(data?.categories || []);
      setCatDeletePermission(data?.permissions?.delete_permission ?? null);
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت دسته‌ها');
    } finally {
      setCatLoading(false);
    }
  }, []);

  const loadSubAll = useCallback(async () => {
    setSubLoading(true);
    try {
      const res = await clientSubcategoriesApi.getCreateData();
      const data = res?.data ?? res;
      setSubCategoriesFull(data?.categories || []);
      setSubCategoriesList(data?.subcategories || []);
      setSubDeletePermission(data?.permissions?.delete_permission ?? null);
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت زیردسته‌ها');
    } finally {
      setSubLoading(false);
    }
  }, []);

  const loadSubForCategory = useCallback(async (categoryId) => {
    setSubLoading(true);
    try {
      const res = await clientSubcategoriesApi.getByCategory(categoryId);
      const list = res?.subcategories ?? res?.data?.subcategories ?? [];
      setSubCategoriesList(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت زیردسته‌ها');
    } finally {
      setSubLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!subPanelOpen) return;
    if (filterCategoryId === 'all') loadSubAll();
    else loadSubForCategory(filterCategoryId);
  }, [subPanelOpen, filterCategoryId, loadSubAll, loadSubForCategory]);

  useEffect(() => {
    if (catPanelOpen) loadCategoriesPanel();
  }, [catPanelOpen, loadCategoriesPanel]);

  const canDeleteCat = catDeletePermission === 'all';
  const canDeleteSub = subDeletePermission === 'all';

  const categoryNameById = useMemo(() => {
    const m = new Map();
    (subCategoriesFull || []).forEach((c) => m.set(c.id, c.category_name));
    return m;
  }, [subCategoriesFull]);

  const afterMutation = async () => {
    if (typeof onRefresh === 'function') {
      await onRefresh();
    }
  };

  const openCatAdd = () => {
    setCatEditing(null);
    catForm.resetFields();
    setCatFormOpen(true);
  };

  const openCatEdit = (record) => {
    setCatEditing(record);
    catForm.setFieldsValue({ category_name: record.category_name });
    setCatFormOpen(true);
  };

  const submitCategory = async () => {
    try {
      const values = await catForm.validateFields();
      setCatSubmitting(true);
      const name = values.category_name.trim();
      if (catEditing) {
        await clientCategoriesApi.update(catEditing.id, name);
        message.success('دسته ویرایش شد');
      } else {
        await clientCategoriesApi.create(name);
        message.success('دسته ثبت شد');
      }
      setCatFormOpen(false);
      await loadCategoriesPanel();
      await afterMutation();
    } catch (e) {
      if (e?.errorFields) return;
      if (e instanceof ValidationError || e?.error?.details) show422Error(e, catForm);
      else message.error(e?.message || 'خطا در ذخیره');
    } finally {
      setCatSubmitting(false);
    }
  };

  const deleteCategory = async (id) => {
    try {
      await clientCategoriesApi.delete(id);
      message.success('دسته حذف شد');
      await loadCategoriesPanel();
      await afterMutation();
    } catch (e) {
      message.error(e?.message || 'خطا در حذف');
    }
  };

  const refreshSubList = () => {
    if (filterCategoryId === 'all') loadSubAll();
    else loadSubForCategory(filterCategoryId);
  };

  const openSubAdd = () => {
    setSubEditing(null);
    subForm.resetFields();
    if (filterCategoryId !== 'all') {
      subForm.setFieldsValue({ category_id: Number(filterCategoryId) });
    }
    setSubFormOpen(true);
  };

  const openSubEdit = (record) => {
    setSubEditing(record);
    subForm.setFieldsValue({ category_name: record.category_name });
    setSubFormOpen(true);
  };

  const submitSubcategory = async () => {
    try {
      const values = await subForm.validateFields();
      setSubSubmitting(true);
      if (subEditing) {
        await clientSubcategoriesApi.update(subEditing.id, values.category_name.trim());
        message.success('زیردسته ویرایش شد');
      } else {
        await clientSubcategoriesApi.create({
          category_name: values.category_name.trim(),
          category_id: values.category_id,
        });
        message.success('زیردسته ثبت شد');
      }
      setSubFormOpen(false);
      refreshSubList();
      await afterMutation();
    } catch (e) {
      if (e?.errorFields) return;
      if (e instanceof ValidationError || e?.error?.details) show422Error(e, subForm);
      else message.error(e?.message || 'خطا در ذخیره');
    } finally {
      setSubSubmitting(false);
    }
  };

  const deleteSubcategory = async (id) => {
    try {
      await clientSubcategoriesApi.delete(id);
      message.success('زیردسته حذف شد');
      refreshSubList();
      await afterMutation();
    } catch (e) {
      message.error(e?.message || 'خطا در حذف');
    }
  };

  const catColumns = [
    {
      title: 'نام دسته',
      dataIndex: 'category_name',
      key: 'category_name',
      render: (t) => <span style={{ fontFamily: 'CustomFont' }}>{t || '—'}</span>,
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space>
          <PermissionGate permission="edit_clients">
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openCatEdit(record)}>
              ویرایش
            </Button>
          </PermissionGate>
          {canDeleteCat ? (
            <PermissionGate permission="edit_clients">
              <Popconfirm title="حذف این دسته؟" okText="بله" cancelText="خیر" onConfirm={() => deleteCategory(record.id)}>
                <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                  حذف
                </Button>
              </Popconfirm>
            </PermissionGate>
          ) : null}
        </Space>
      ),
    },
  ];

  const subColumns = [
    {
      title: 'دسته والد',
      key: 'parent',
      width: 180,
      render: (_, record) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {categoryNameById.get(record.category_id) || '—'}
        </span>
      ),
    },
    {
      title: 'نام زیردسته',
      dataIndex: 'category_name',
      key: 'category_name',
      render: (t) => <span style={{ fontFamily: 'CustomFont' }}>{t || '—'}</span>,
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space>
          <PermissionGate permission="edit_clients">
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openSubEdit(record)}>
              ویرایش
            </Button>
          </PermissionGate>
          {canDeleteSub ? (
            <PermissionGate permission="edit_clients">
              <Popconfirm title="حذف این زیردسته؟" okText="بله" cancelText="خیر" onConfirm={() => deleteSubcategory(record.id)}>
                <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                  حذف
                </Button>
              </Popconfirm>
            </PermissionGate>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <PermissionGate permission="edit_clients">
          <Button type="default" icon={<FolderOutlined />} onClick={() => setCatPanelOpen(true)} style={{ fontFamily: 'CustomFont' }}>
            مدیریت دسته‌ها
          </Button>
          <Button type="default" icon={<FolderOpenOutlined />} onClick={() => setSubPanelOpen(true)} style={{ fontFamily: 'CustomFont' }}>
            مدیریت زیردسته‌ها
          </Button>
        </PermissionGate>
      </div>

      <Modal
        title={<span style={{ fontFamily: 'CustomFont' }}>دسته‌های مشتری</span>}
        open={catPanelOpen}
        onCancel={() => setCatPanelOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
      >
        <div className="flex justify-end mb-3">
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadCategoriesPanel} loading={catLoading}>
              بروزرسانی
            </Button>
            <PermissionGate permission="edit_clients">
              <Button type="primary" icon={<PlusOutlined />} onClick={openCatAdd} className={PANEL_PRIMARY_BTN_CLASS}>
                دسته جدید
              </Button>
            </PermissionGate>
          </Space>
        </div>
        <Table
          rowKey="id"
          size="small"
          loading={catLoading}
          columns={catColumns}
          dataSource={categories}
          pagination={{ pageSize: 8 }}
        />
      </Modal>

      <Modal
        title={<span style={{ fontFamily: 'CustomFont' }}>{catEditing ? 'ویرایش دسته' : 'دسته جدید'}</span>}
        open={catFormOpen}
        onCancel={() => setCatFormOpen(false)}
        onOk={submitCategory}
        confirmLoading={catSubmitting}
        okText="ذخیره"
        cancelText="لغو"
        destroyOnClose
      >
        <Form form={catForm} layout="vertical" style={{ fontFamily: 'CustomFont' }}>
          <Form.Item name="category_name" label="نام دسته" rules={[{ required: true, message: 'نام را وارد کنید' }]}>
            <Input placeholder="نام دسته" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={<span style={{ fontFamily: 'CustomFont' }}>زیردسته‌های مشتری</span>}
        open={subPanelOpen}
        onCancel={() => setSubPanelOpen(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <Select
            value={filterCategoryId}
            onChange={setFilterCategoryId}
            style={{ minWidth: 200, fontFamily: 'CustomFont' }}
            placeholder="فیلتر دسته"
          >
            <Option value="all">همه دسته‌ها</Option>
            {(subCategoriesFull || []).map((c) => (
              <Option key={c.id} value={String(c.id)}>
                {c.category_name}
              </Option>
            ))}
          </Select>
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={refreshSubList} loading={subLoading}>
              بروزرسانی
            </Button>
            <PermissionGate permission="edit_clients">
              <Button type="primary" icon={<PlusOutlined />} onClick={openSubAdd} className={PANEL_PRIMARY_BTN_CLASS}>
                زیردسته جدید
              </Button>
            </PermissionGate>
          </Space>
        </div>
        <Table
          rowKey="id"
          size="small"
          loading={subLoading}
          columns={subColumns}
          dataSource={subCategoriesList}
          pagination={{ pageSize: 8 }}
        />
      </Modal>

      <Modal
        title={<span style={{ fontFamily: 'CustomFont' }}>{subEditing ? 'ویرایش زیردسته' : 'زیردسته جدید'}</span>}
        open={subFormOpen}
        onCancel={() => setSubFormOpen(false)}
        onOk={submitSubcategory}
        confirmLoading={subSubmitting}
        okText="ذخیره"
        cancelText="لغو"
        width={480}
        destroyOnClose
      >
        <Form form={subForm} layout="vertical" style={{ fontFamily: 'CustomFont' }}>
          {!subEditing && (
            <Form.Item name="category_id" label="دسته" rules={[{ required: true, message: 'دسته را انتخاب کنید' }]}>
              <Select placeholder="انتخاب دسته" showSearch optionFilterProp="children">
                {(subCategoriesFull || []).map((c) => (
                  <Option key={c.id} value={c.id}>
                    {c.category_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item name="category_name" label="نام زیردسته" rules={[{ required: true, message: 'نام را وارد کنید' }]}>
            <Input placeholder="نام زیردسته" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ClientTaxonomyManager;
